/**
 * @fileoverview Historique citoyen mocké.
 *
 * La liste est paginée côté UI (« Charger plus ») pour refléter la
 * pagination par curseur de l'API (`GET /me/payments?cursor=…`) : au
 * branchement, le clic déclenchera le fetch de la page suivante au lieu
 * d'étendre une liste locale. Pas de stockage navigateur : l'état de
 * pagination vit en mémoire et repart de la première page à chaque visite.
 */

import { useState } from 'react';
import {
  Amount,
  Button,
  InfoNotice,
  LiquidationNumber,
  PaymentProofActions,
  StatusBadge,
} from '@gnpc/ui';
import { MOCK_CONTRAVENTIONS } from '../data/mock.ts';

/** Taille de page mock — alignée sur un `limit` raisonnable côté API. */
const HISTORY_PAGE_SIZE = 4;

export function HistoryPage(): JSX.Element {
  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE_SIZE);
  const visibleItems = MOCK_CONTRAVENTIONS.slice(0, visibleCount);
  const hasMore = visibleCount < MOCK_CONTRAVENTIONS.length;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header className="space-y-2">
        <p className="text-quipux-orange-600 text-sm font-medium">Historique</p>
        <h1 className="text-quipux-navy-950 text-3xl font-semibold tracking-tight">
          Mes paiements
        </h1>
        <p className="text-muted-foreground text-sm leading-6">
          Les documents restent accessibles après le retour du guichet.
        </p>
      </header>

      <InfoNotice title="Documents de paiement">
        Les justificatifs de débit apparaissent dans cette liste. La quittance officielle se
        télécharge depuis votre compte Quipux.
      </InfoNotice>

      <div className="grid gap-3">
        {visibleItems.map((item) => (
          <article
            key={item.liquidationNumber}
            className="border-border bg-card rounded-lg border p-4"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-2">
                <LiquidationNumber value={item.liquidationNumber} copyable />
                <p className="text-quipux-navy-950 text-sm font-medium">{item.infractionType}</p>
                <div className="flex gap-2">
                  <StatusBadge kind="payment" status="PAID" compact />
                  <StatusBadge
                    kind="sgi"
                    status={item.liquidationNumber.endsWith('5') ? 'RECORDED' : 'PENDING'}
                    compact
                  />
                </div>
              </div>
              <Amount value={item.amount} compact />
            </div>
            <div className="mt-4">
              <PaymentProofActions debitNoteAvailable />
            </div>
          </article>
        ))}
      </div>

      <div className="flex flex-col items-center gap-2 pb-4">
        <p className="text-muted-foreground text-xs">
          {visibleItems.length} paiement(s) affiché(s) sur {MOCK_CONTRAVENTIONS.length}
        </p>
        {hasMore && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setVisibleCount((current) => current + HISTORY_PAGE_SIZE)}
          >
            Charger plus
          </Button>
        )}
      </div>
    </div>
  );
}
